import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Edit3,
  Trash2,
  Save,
  X,
  Search,
  RefreshCw,
  FileX,
  AlertCircle,
  CheckCircle,
  ShieldCheck,
  Calendar,
  MessageSquare,
  FileText,
  ArrowRightLeft
} from 'lucide-react';
import { initializeApp, getApps } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';

// =============================================================================
// SERVICE CLASSES
// =============================================================================

// Service class for First Sale Ignore List
class FirstSaleIgnoreListServiceClass {
  constructor() {
    this.functions = null;
    this.initialized = false;
    this.tableName = 'first_sale_error_ignore_list';
  }

  async initialize() {
    if (this.initialized) return;
    try {
      if (getApps().length === 0) {
        const firebaseConfig = typeof window.__firebase_config !== 'undefined' ? JSON.parse(window.__firebase_config) : {};
        initializeApp(firebaseConfig);
      }
      this.functions = getFunctions();
      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize FirstSaleIgnoreListService:', error);
      throw error;
    }
  }

  async getIgnoreList() {
    await this.initialize();
    try {
      const getLookupTableData = httpsCallable(this.functions, 'getLookupTableData');
      const result = await getLookupTableData({ tableName: this.tableName });
      return result.data.data || [];
    } catch (error) {
      console.error('Error getting First Sale ignore list:', error);
      throw new Error(`Failed to get ignore list: ${error.message}`);
    }
  }

  async addToIgnoreList(record) {
    await this.initialize();
    try {
      const addLookupRecord = httpsCallable(this.functions, 'addLookupRecord');
      const result = await addLookupRecord({ tableName: this.tableName, record });
      return result.data.data;
    } catch (error) {
      throw new Error(`Failed to add to ignore list: ${error.message}`, { cause: error.details });
    }
  }

  async updateIgnoreListRecord(id, record) {
    await this.initialize();
    try {
      const updateLookupRecord = httpsCallable(this.functions, 'updateLookupRecord');
      const result = await updateLookupRecord({ tableName: this.tableName, id, record });
      return result.data.data.updatedRecord;
    } catch (error) {
      throw new Error(`Failed to update record: ${error.message}`, { cause: error.details });
    }
  }

  async removeFromIgnoreList(id) {
    await this.initialize();
    try {
      const deleteLookupRecord = httpsCallable(this.functions, 'deleteLookupRecord');
      const result = await deleteLookupRecord({ tableName: this.tableName, id });
      return result.data.data.success;
    } catch (error) {
      throw new Error(`Failed to remove from ignore list: ${error.message}`, { cause: error.details });
    }
  }
}

// Service class for Terms Changes
class TermsChangesServiceClass {
  constructor() {
    this.functions = null;
    this.initialized = false;
    this.tableName = 'commercial_invoice_terms_changes';
  }

  async initialize() {
    if (this.initialized) return;
    try {
      if (getApps().length === 0) {
        const firebaseConfig = typeof window.__firebase_config !== 'undefined' ? JSON.parse(window.__firebase_config) : {};
        initializeApp(firebaseConfig);
      }
      this.functions = getFunctions();
      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize TermsChangesService:', error);
      throw error;
    }
  }

  async getTermsChanges() {
    await this.initialize();
    try {
      const getLookupTableData = httpsCallable(this.functions, 'getLookupTableData');
      const result = await getLookupTableData({ tableName: this.tableName });
      return result.data.data || [];
    } catch (error) {
      console.error('Error getting terms changes:', error);
      throw new Error(`Failed to get terms changes: ${error.message}`);
    }
  }

  async addTermsChange(record) {
    await this.initialize();
    try {
      const addLookupRecord = httpsCallable(this.functions, 'addLookupRecord');
      const result = await addLookupRecord({ tableName: this.tableName, record });
      return result.data.data;
    } catch (error) {
      throw new Error(`Failed to add terms change: ${error.message}`, { cause: error.details });
    }
  }

  async updateTermsChange(id, record) {
    await this.initialize();
    try {
      const updateLookupRecord = httpsCallable(this.functions, 'updateLookupRecord');
      const result = await updateLookupRecord({ tableName: this.tableName, id, record });
      return result.data.data.updatedRecord;
    } catch (error) {
      throw new Error(`Failed to update terms change: ${error.message}`, { cause: error.details });
    }
  }

  async removeTermsChange(id) {
    await this.initialize();
    try {
      const deleteLookupRecord = httpsCallable(this.functions, 'deleteLookupRecord');
      const result = await deleteLookupRecord({ tableName: this.tableName, id });
      return result.data.data.success;
    } catch (error) {
      throw new Error(`Failed to remove terms change: ${error.message}`, { cause: error.details });
    }
  }
}

export const FirstSaleIgnoreListService = new FirstSaleIgnoreListServiceClass();
export const TermsChangesService = new TermsChangesServiceClass();

// =============================================================================
// FIELD DEFINITIONS
// =============================================================================

const IGNORE_LIST_FIELDS = [
  {
    key: 'commercial_invoice',
    label: 'Commercial Invoice #',
    type: 'text',
    required: true,
    placeholder: 'Enter commercial invoice number',
    description: 'The exact commercial invoice number to exclude from the First Sale Error Report'
  },
  {
    key: 'error_type',
    label: 'Error Type',
    type: 'select',
    required: true,
    options: [
      { value: 'fs', label: 'FS/FS3 Terms (FOB Variance > -10%)' },
      { value: 'wi', label: 'WI/W3 Terms (FOB Variance < -4%)' },
      { value: 'overvalued', label: 'Over-Valued (Custom > FOB by 3%+)' },
      { value: 'verylow', label: 'Very Low (Custom < 75% of FOB)' },
      { value: 'all', label: 'All Error Types' }
    ],
    placeholder: 'Select error type to ignore'
  },
  {
    key: 'reason',
    label: 'Reason for Ignoring',
    type: 'select',
    required: true,
    options: [
      { value: 'verified_correct', label: 'Verified as Correct' },
      { value: 'special_pricing', label: 'Special Pricing Arrangement' },
      { value: 'sample_shipment', label: 'Sample Shipment' },
      { value: 'promotional', label: 'Promotional / Marketing' },
      { value: 'negotiated_rate', label: 'Negotiated Rate' },
      { value: 'data_timing', label: 'Data Timing Issue' },
      { value: 'under_review', label: 'Under Review' },
      { value: 'other', label: 'Other' }
    ],
    placeholder: 'Select a reason'
  },
  {
    key: 'notes',
    label: 'Additional Notes',
    type: 'textarea',
    required: false,
    placeholder: 'Add any additional details...',
    description: 'Optional notes for future reference'
  },
  {
    key: 'added_by',
    label: 'Added By',
    type: 'text',
    required: false,
    placeholder: 'Your name or ID'
  },
  {
    key: 'active',
    label: 'Active',
    type: 'boolean',
    required: false,
    description: 'Uncheck to temporarily stop ignoring this invoice'
  }
];

const TERMS_CHANGE_FIELDS = [
  {
    key: 'commercial_invoice',
    label: 'Commercial Invoice #',
    type: 'text',
    required: true,
    placeholder: 'Enter commercial invoice number',
    description: 'The commercial invoice to change terms for'
  },
  {
    key: 'new_terms',
    label: 'New Terms',
    type: 'select',
    required: true,
    options: [
      { value: 'FS', label: 'FS - First Sale' },
      { value: 'FS3', label: 'FS3 - First Sale 3' },
      { value: 'WI', label: 'WI - Wholesale Import' },
      { value: 'W3', label: 'W3 - Wholesale 3' },
      { value: 'FOB', label: 'FOB - Free On Board' },
      { value: 'CIF', label: 'CIF - Cost Insurance Freight' },
      { value: 'DDP', label: 'DDP - Delivered Duty Paid' }
    ],
    placeholder: 'Select new terms'
  },
  {
    key: 'reason',
    label: 'Reason for Change',
    type: 'select',
    required: true,
    options: [
      { value: 'incorrect_entry', label: 'Incorrect Original Entry' },
      { value: 'terms_renegotiated', label: 'Terms Renegotiated' },
      { value: 'customer_request', label: 'Customer Request' },
      { value: 'compliance_adjustment', label: 'Compliance Adjustment' },
      { value: 'data_correction', label: 'Data Correction' },
      { value: 'other', label: 'Other' }
    ],
    placeholder: 'Select a reason'
  },
  {
    key: 'notes',
    label: 'Additional Notes',
    type: 'textarea',
    required: false,
    placeholder: 'Add any additional details about this terms change...',
    description: 'Optional notes for audit trail'
  },
  {
    key: 'added_by',
    label: 'Added By',
    type: 'text',
    required: false,
    placeholder: 'Your name or ID'
  },
  {
    key: 'active',
    label: 'Active',
    type: 'boolean',
    required: false,
    description: 'Uncheck to temporarily disable this terms override'
  }
];

// =============================================================================
// LABEL MAPPINGS
// =============================================================================

const REASON_LABELS = {
  verified_correct: 'Verified as Correct',
  special_pricing: 'Special Pricing',
  sample_shipment: 'Sample Shipment',
  promotional: 'Promotional',
  negotiated_rate: 'Negotiated Rate',
  data_timing: 'Data Timing Issue',
  under_review: 'Under Review',
  other: 'Other'
};

const ERROR_TYPE_LABELS = {
  fs: 'FS/FS3 Terms',
  wi: 'WI/W3 Terms',
  overvalued: 'Over-Valued',
  verylow: 'Very Low',
  all: 'All Types'
};

const TERMS_REASON_LABELS = {
  incorrect_entry: 'Incorrect Entry',
  terms_renegotiated: 'Renegotiated',
  customer_request: 'Customer Request',
  compliance_adjustment: 'Compliance',
  data_correction: 'Data Correction',
  other: 'Other'
};

const REASON_COLORS = {
  verified_correct: 'bg-green-100 text-green-800',
  special_pricing: 'bg-blue-100 text-blue-800',
  sample_shipment: 'bg-purple-100 text-purple-800',
  promotional: 'bg-pink-100 text-pink-800',
  negotiated_rate: 'bg-indigo-100 text-indigo-800',
  data_timing: 'bg-yellow-100 text-yellow-800',
  under_review: 'bg-orange-100 text-orange-800',
  other: 'bg-slate-100 text-slate-800'
};

const ERROR_TYPE_COLORS = {
  fs: 'bg-blue-100 text-blue-800',
  wi: 'bg-orange-100 text-orange-800',
  overvalued: 'bg-red-100 text-red-800',
  verylow: 'bg-purple-100 text-purple-800',
  all: 'bg-gray-100 text-gray-800'
};

const TERMS_COLORS = {
  FS: 'bg-blue-100 text-blue-800',
  FS3: 'bg-blue-100 text-blue-800',
  WI: 'bg-orange-100 text-orange-800',
  W3: 'bg-orange-100 text-orange-800',
  FOB: 'bg-green-100 text-green-800',
  CIF: 'bg-purple-100 text-purple-800',
  DDP: 'bg-indigo-100 text-indigo-800'
};

const TERMS_REASON_COLORS = {
  incorrect_entry: 'bg-red-100 text-red-800',
  terms_renegotiated: 'bg-blue-100 text-blue-800',
  customer_request: 'bg-purple-100 text-purple-800',
  compliance_adjustment: 'bg-yellow-100 text-yellow-800',
  data_correction: 'bg-orange-100 text-orange-800',
  other: 'bg-slate-100 text-slate-800'
};

// =============================================================================
// GENERIC FORM COMPONENT
// =============================================================================

function GenericForm({ fields, record, onSave, onCancel, isEditing, title, subtitle, icon: Icon, color = 'green' }) {
  const [formData, setFormData] = useState(record || {});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      const initialData = { active: true };
      fields.forEach(field => {
        if (field.type === 'boolean') {
          initialData[field.key] = true;
        }
      });
      setFormData(prev => ({ ...initialData, ...prev }));
    }
  }, [isEditing, fields]);

  const validateForm = () => {
    const newErrors = {};
    fields.forEach(field => {
      if (field.required && !formData[field.key]) {
        newErrors[field.key] = `${field.label} is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      setSaving(true);
      try {
        await onSave(formData);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleInputChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: null }));
    }
  };

  const colorClasses = {
    green: { bg: 'from-green-50 to-emerald-50', icon: 'bg-green-100', iconText: 'text-green-600', ring: 'focus:ring-green-500', btn: 'bg-green-600 hover:bg-green-700', checkbox: 'text-green-600 focus:ring-green-500' },
    blue: { bg: 'from-blue-50 to-cyan-50', icon: 'bg-blue-100', iconText: 'text-blue-600', ring: 'focus:ring-blue-500', btn: 'bg-blue-600 hover:bg-blue-700', checkbox: 'text-blue-600 focus:ring-blue-500' }
  };
  const colors = colorClasses[color] || colorClasses.green;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className={`px-6 py-4 border-b border-gray-200 bg-gradient-to-r ${colors.bg}`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 ${colors.icon} rounded-lg`}>
              <Icon className={`h-5 w-5 ${colors.iconText}`} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-600">{subtitle}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {fields.map(field => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {field.description && (
                <p className="text-xs text-gray-500 mb-2">{field.description}</p>
              )}

              {field.type === 'text' && (
                <input
                  type="text"
                  value={formData[field.key] || ''}
                  onChange={(e) => handleInputChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  disabled={saving}
                  className={`w-full px-3 py-2 border rounded-lg ${colors.ring} focus:border-transparent ${
                    errors[field.key] ? 'border-red-500' : 'border-gray-300'
                  } ${saving ? 'bg-gray-50' : ''}`}
                />
              )}

              {field.type === 'select' && (
                <select
                  value={formData[field.key] || ''}
                  onChange={(e) => handleInputChange(field.key, e.target.value)}
                  disabled={saving}
                  className={`w-full px-3 py-2 border rounded-lg ${colors.ring} focus:border-transparent ${
                    errors[field.key] ? 'border-red-500' : 'border-gray-300'
                  } ${saving ? 'bg-gray-50' : ''}`}
                >
                  <option value="">{field.placeholder}</option>
                  {field.options.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              )}

              {field.type === 'textarea' && (
                <textarea
                  value={formData[field.key] || ''}
                  onChange={(e) => handleInputChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  rows={3}
                  disabled={saving}
                  className={`w-full px-3 py-2 border rounded-lg ${colors.ring} focus:border-transparent ${
                    errors[field.key] ? 'border-red-500' : 'border-gray-300'
                  } ${saving ? 'bg-gray-50' : ''}`}
                />
              )}

              {field.type === 'boolean' && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={!!formData[field.key]}
                    onChange={(e) => handleInputChange(field.key, e.target.checked)}
                    disabled={saving}
                    className={`h-4 w-4 ${colors.checkbox} border-gray-300 rounded`}
                  />
                  <span className="ml-2 text-sm text-gray-600">
                    {formData[field.key] ? 'Active' : 'Inactive'}
                  </span>
                </div>
              )}

              {errors[field.key] && (
                <p className="text-sm text-red-500 mt-1">{errors[field.key]}</p>
              )}
            </div>
          ))}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <X className="h-4 w-4 inline mr-1" />
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className={`px-4 py-2 text-sm font-medium text-white ${colors.btn} border border-transparent rounded-lg disabled:opacity-50 flex items-center`}
            >
              {saving ? (
                <RefreshCw className="h-4 w-4 inline mr-1 animate-spin" />
              ) : (
                <Save className="h-4 w-4 inline mr-1" />
              )}
              {saving ? 'Saving...' : (isEditing ? 'Update' : 'Add')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function FirstSaleSettings() {
  // Ignore List State
  const [ignoreData, setIgnoreData] = useState([]);
  const [ignoreSearchTerm, setIgnoreSearchTerm] = useState('');
  const [showIgnoreForm, setShowIgnoreForm] = useState(false);
  const [editingIgnoreRecord, setEditingIgnoreRecord] = useState(null);
  const [deleteIgnoreConfirm, setDeleteIgnoreConfirm] = useState(null);

  // Terms Changes State
  const [termsData, setTermsData] = useState([]);
  const [termsSearchTerm, setTermsSearchTerm] = useState('');
  const [showTermsForm, setShowTermsForm] = useState(false);
  const [editingTermsRecord, setEditingTermsRecord] = useState(null);
  const [deleteTermsConfirm, setDeleteTermsConfirm] = useState(null);

  // Shared State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Load Data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ignoreList, termsList] = await Promise.all([
        FirstSaleIgnoreListService.getIgnoreList(),
        TermsChangesService.getTermsChanges()
      ]);
      setIgnoreData(Array.isArray(ignoreList) ? ignoreList : []);
      setTermsData(Array.isArray(termsList) ? termsList : []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(`Failed to load data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Ignore List Handlers
  const handleIgnoreSave = async (formData) => {
    setError(null);
    try {
      if (editingIgnoreRecord) {
        const updated = await FirstSaleIgnoreListService.updateIgnoreListRecord(editingIgnoreRecord.id, formData);
        setIgnoreData(prev => prev.map(item => item.id === updated.id ? { ...item, ...updated } : item));
        setSuccessMessage(`Updated "${formData.commercial_invoice}" in ignore list`);
      } else {
        const created = await FirstSaleIgnoreListService.addToIgnoreList(formData);
        setIgnoreData(prev => [created, ...prev]);
        setSuccessMessage(`Added "${formData.commercial_invoice}" to ignore list`);
      }
      setShowIgnoreForm(false);
      setEditingIgnoreRecord(null);
    } catch (err) {
      setError(err.message.includes('streaming buffer')
        ? 'Please wait 90-120 minutes for BigQuery streaming buffer to clear.'
        : `Failed to save: ${err.message}`);
    }
  };

  const handleIgnoreDelete = async (record) => {
    setError(null);
    try {
      await FirstSaleIgnoreListService.removeFromIgnoreList(record.id);
      setIgnoreData(prev => prev.filter(item => item.id !== record.id));
      setDeleteIgnoreConfirm(null);
      setSuccessMessage(`Removed "${record.commercial_invoice}" from ignore list`);
    } catch (err) {
      setError(err.message.includes('streaming buffer')
        ? 'Please wait 90-120 minutes for BigQuery streaming buffer to clear.'
        : `Failed to delete: ${err.message}`);
    }
  };

  // Terms Changes Handlers
  const handleTermsSave = async (formData) => {
    setError(null);
    try {
      if (editingTermsRecord) {
        const updated = await TermsChangesService.updateTermsChange(editingTermsRecord.id, formData);
        setTermsData(prev => prev.map(item => item.id === updated.id ? { ...item, ...updated } : item));
        setSuccessMessage(`Updated terms for "${formData.commercial_invoice}"`);
      } else {
        const created = await TermsChangesService.addTermsChange(formData);
        setTermsData(prev => [created, ...prev]);
        setSuccessMessage(`Added terms change for "${formData.commercial_invoice}"`);
      }
      setShowTermsForm(false);
      setEditingTermsRecord(null);
    } catch (err) {
      setError(err.message.includes('streaming buffer')
        ? 'Please wait 90-120 minutes for BigQuery streaming buffer to clear.'
        : `Failed to save: ${err.message}`);
    }
  };

  const handleTermsDelete = async (record) => {
    setError(null);
    try {
      await TermsChangesService.removeTermsChange(record.id);
      setTermsData(prev => prev.filter(item => item.id !== record.id));
      setDeleteTermsConfirm(null);
      setSuccessMessage(`Removed terms change for "${record.commercial_invoice}"`);
    } catch (err) {
      setError(err.message.includes('streaming buffer')
        ? 'Please wait 90-120 minutes for BigQuery streaming buffer to clear.'
        : `Failed to delete: ${err.message}`);
    }
  };

  // Filtered Data
  const filteredIgnoreData = ignoreData.filter(record =>
    Object.values(record).some(value =>
      value?.toString().toLowerCase().includes(ignoreSearchTerm.toLowerCase())
    )
  );

  const filteredTermsData = termsData.filter(record =>
    Object.values(record).some(value =>
      value?.toString().toLowerCase().includes(termsSearchTerm.toLowerCase())
    )
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 text-green-600 animate-spin" />
        <span className="ml-2 text-gray-600">Loading First Sale Settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 p-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-green-100 rounded-xl">
            <ShieldCheck className="h-8 w-8 text-green-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">First Sale Settings</h1>
            <p className="text-gray-600">
              Manage invoice ignore list and terms overrides for First Sale reporting
            </p>
          </div>
        </div>
      </div>

      {/* Messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
          <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
          <span className="text-green-700">{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} className="ml-auto text-green-600 hover:text-green-800">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
          <div className="flex-grow">
            <span className="font-semibold text-red-800">Error: </span>
            <span className="text-red-700">{error}</span>
          </div>
          <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* ====================== IGNORE LIST SECTION ====================== */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileX className="h-5 w-5 text-green-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Commercial Invoice Ignore List</h2>
                <p className="text-sm text-gray-600">Invoices excluded from the First Sale Error Report</p>
              </div>
            </div>
            <button
              onClick={() => { setEditingIgnoreRecord(null); setShowIgnoreForm(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Plus className="h-4 w-4" />
              Add Invoice
            </button>
          </div>
        </div>

        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search ignore list..."
                value={ignoreSearchTerm}
                onChange={(e) => setIgnoreSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="px-3 py-1 bg-white rounded-full border">
                Total: <span className="font-semibold">{filteredIgnoreData.length}</span>
              </span>
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full border border-green-200">
                Active: <span className="font-semibold">{filteredIgnoreData.filter(r => r.active !== false).length}</span>
              </span>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredIgnoreData.map((record) => (
                <tr key={record.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">{record.commercial_invoice || '-'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${ERROR_TYPE_COLORS[record.error_type] || 'bg-gray-100 text-gray-800'}`}>
                      {ERROR_TYPE_LABELS[record.error_type] || record.error_type || '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${REASON_COLORS[record.reason] || 'bg-gray-100 text-gray-800'}`}>
                      {REASON_LABELS[record.reason] || record.reason || '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="max-w-xs truncate text-sm text-gray-600" title={record.notes}>
                      {record.notes || <span className="text-gray-400">-</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${record.active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {record.active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button onClick={() => { setEditingIgnoreRecord(record); setShowIgnoreForm(true); }} className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded mr-1">
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button onClick={() => setDeleteIgnoreConfirm(record)} className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredIgnoreData.length === 0 && (
          <div className="text-center py-12">
            <FileX className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No ignored invoices</h3>
            <p className="mt-1 text-sm text-gray-500">Add invoices to exclude from error reporting</p>
          </div>
        )}
      </div>

      {/* ====================== TERMS CHANGES SECTION ====================== */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ArrowRightLeft className="h-5 w-5 text-blue-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Commercial Invoice Terms Changes</h2>
                <p className="text-sm text-gray-600">Override terms for specific commercial invoices</p>
              </div>
            </div>
            <button
              onClick={() => { setEditingTermsRecord(null); setShowTermsForm(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              Add Terms Change
            </button>
          </div>
        </div>

        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search terms changes..."
                value={termsSearchTerm}
                onChange={(e) => setTermsSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="px-3 py-1 bg-white rounded-full border">
                Total: <span className="font-semibold">{filteredTermsData.length}</span>
              </span>
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full border border-blue-200">
                Active: <span className="font-semibold">{filteredTermsData.filter(r => r.active !== false).length}</span>
              </span>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">New Terms</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added By</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTermsData.map((record) => (
                <tr key={record.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">{record.commercial_invoice || '-'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-bold rounded-full ${TERMS_COLORS[record.new_terms] || 'bg-gray-100 text-gray-800'}`}>
                      {record.new_terms || '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${TERMS_REASON_COLORS[record.reason] || 'bg-gray-100 text-gray-800'}`}>
                      {TERMS_REASON_LABELS[record.reason] || record.reason || '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="max-w-xs truncate text-sm text-gray-600" title={record.notes}>
                      {record.notes || <span className="text-gray-400">-</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {record.added_by || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${record.active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {record.active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button onClick={() => { setEditingTermsRecord(record); setShowTermsForm(true); }} className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded mr-1">
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button onClick={() => setDeleteTermsConfirm(record)} className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredTermsData.length === 0 && (
          <div className="text-center py-12">
            <ArrowRightLeft className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No terms changes</h3>
            <p className="mt-1 text-sm text-gray-500">Add terms overrides for specific invoices</p>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-800">How this works</h4>
            <p className="mt-1 text-sm text-blue-700">
              <strong>Ignore List:</strong> Invoices added here will be excluded from the First Sale Error Report.<br />
              <strong>Terms Changes:</strong> Override the terms for specific invoices. These changes will be joined with the data in views and reports.
            </p>
          </div>
        </div>
      </div>

      {/* Ignore List Form Modal */}
      {showIgnoreForm && (
        <GenericForm
          fields={IGNORE_LIST_FIELDS}
          record={editingIgnoreRecord}
          onSave={handleIgnoreSave}
          onCancel={() => { setShowIgnoreForm(false); setEditingIgnoreRecord(null); }}
          isEditing={!!editingIgnoreRecord}
          title={editingIgnoreRecord ? 'Edit Ignore Entry' : 'Add to Ignore List'}
          subtitle={editingIgnoreRecord ? 'Update the ignored invoice details' : 'Exclude an invoice from error reporting'}
          icon={ShieldCheck}
          color="green"
        />
      )}

      {/* Terms Change Form Modal */}
      {showTermsForm && (
        <GenericForm
          fields={TERMS_CHANGE_FIELDS}
          record={editingTermsRecord}
          onSave={handleTermsSave}
          onCancel={() => { setShowTermsForm(false); setEditingTermsRecord(null); }}
          isEditing={!!editingTermsRecord}
          title={editingTermsRecord ? 'Edit Terms Change' : 'Add Terms Change'}
          subtitle={editingTermsRecord ? 'Update the terms override' : 'Override terms for a commercial invoice'}
          icon={ArrowRightLeft}
          color="blue"
        />
      )}

      {/* Delete Ignore Confirmation */}
      {deleteIgnoreConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Remove from Ignore List?</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Invoice <strong>{deleteIgnoreConfirm.commercial_invoice}</strong> will appear in error reports again.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteIgnoreConfirm(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={() => handleIgnoreDelete(deleteIgnoreConfirm)} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Terms Confirmation */}
      {deleteTermsConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Remove Terms Change?</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Terms override for <strong>{deleteTermsConfirm.commercial_invoice}</strong> will be removed.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteTermsConfirm(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={() => handleTermsDelete(deleteTermsConfirm)} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
